import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  choose,
  emptySelection,
  follow,
  reconcile,
  stopFollowing as dropFollow,
} from '../services/stage-selection'
import { buildStageItems } from '../services/stage-items'
import type { StageItem, StageItemKind } from '../services/stage-items'
import {
  applyItemCount,
  applyOff,
  applyPresence,
  applyPush,
  canToggleSync as canToggleSyncFor,
  freshJoins,
  hostCanTurnOff as hostCanTurnOffFor,
  isLockedFollower,
  isSyncOwner as isSyncOwnerFor,
  ownerLeft,
  resolveTarget,
  retarget,
  startSync,
  type RoomRoleName,
  type StageSync,
} from '../services/stage-sync'
import { vclog } from './vc-log'

export type { StageItem, StageItemKind }

/**
 * Everything currently presentable, as one list, plus which of them this
 * client is looking at.
 *
 * The stage used to be decided by a chain of truthiness — screen share, else
 * whiteboard, else document, else video — so starting a second thing hid the
 * first and the only way back was to close whatever now sat in front. Nothing
 * was lost when that happened, which is what made it confusing: the whiteboard
 * was still open and still shared, it simply had no way to be looked at.
 *
 * Collecting the sources into one list makes "what is running" and "what am I
 * watching" separate questions. Starting something no longer takes anything
 * away; it adds a chip to the bar and moves the viewer, and every other source
 * stays exactly where it was.
 *
 * The list is derived, never stored. Each source already owns its own
 * lifecycle — screen shares come from producers, the whiteboard and shared
 * media are room state — and a second copy here would be one more thing to
 * keep true. This hook only decides ordering and selection.
 */

type StageSyncEnvelope = {
  __inmeet?: string
  /** 'sync' pulls the room once, or (with mode 'on') pushes a sync in force; 'sync-off' ends one. */
  action?: string
  kind?: StageItemKind
  syncId?: string
  by?: string
  /** Present on pushes of a sync that stays on. Absent: a one-off pull, as mobile sends. */
  mode?: 'on'
  ownerName?: string
  /** Re-sent for a newcomer; clients already in the sync must not be pulled back by it. */
  reannounce?: boolean
}

export type UseStageContentParams = {
  /** Screen shares, in the order the stage already had them. */
  screens: { key: string; name: string; isLocal: boolean; peerId?: string }[]
  whiteboardActive: boolean
  whiteboardLabel: string
  /**
   * Every document on the room's stage, oldest first — not just the top one.
   * Two participants presenting one document each is two chips, and neither
   * takes the other's place.
   */
  documents: { id: string; name: string }[]
  /** Every video on the room's stage, oldest first. */
  videos: { id: string; name: string }[]
  /** This client's peerId, used to make its own screen share portable. */
  localPeerId: string | null | undefined
  /** Host and co-hosts may pull the room onto what they are looking at. */
  isModerator: boolean
  /** Only the host may turn off a sync someone else turned on. */
  myRole: RoomRoleName
  /** Shown to the other moderators as "Sync on by <name>". */
  localName: string
  /** Who sent a 'sync-off', to accept it only from the owner or the host. */
  roleOf: (peerId: string | undefined) => RoomRoleName
  /** Channel is up; before this, sending is pointless and receiving is empty. */
  ready: boolean
  subscribe: (event: string, handler: (payload: unknown) => void) => () => void
  sendEvent: (event: string, payload: Record<string, unknown>) => void
}

export type UseStageContentResult = {
  items: StageItem[]
  /** The item on the stage, or null when nothing is being presented. */
  active: StageItem | null
  activeKind: StageItemKind | null
  /** Look at something else. Counts as a deliberate break from the host. */
  select: (key: string) => void
  /** More than one thing is running, so the switcher is worth showing. */
  hasChoice: boolean
  /**
   * Moderator only. Off: turn the sync on, with this client as its owner.
   * On: the owner turns it off.
   */
  syncRoom: () => void
  /** The sync control is shown: a moderator with nothing synced, or the owner. */
  canSync: boolean
  /** A sync is in force and this client is the one driving it. */
  isSyncOwner: boolean
  /** A sync is in force, whoever owns it. */
  syncOn: boolean
  /** Who turned it on, for the "Sync on by" chip other moderators see. */
  syncOwnerName: string | null
  /** The host, when someone else's sync is on: may turn it off. */
  hostCanTurnOff: boolean
  /** Turn off a sync this client does not own. Host only; a no-op otherwise. */
  turnOffSync: () => void
  /** This client is on the host's pick and has not since chosen otherwise. */
  following: boolean
  /** Following someone else's sync: the opt-out is not offered. */
  followingLocked: boolean
  /** Stop following without changing what is on screen. */
  stopFollowing: () => void
}

export function useStageContent({
  screens,
  whiteboardActive,
  whiteboardLabel,
  documents,
  videos,
  localPeerId,
  isModerator,
  myRole,
  localName,
  roleOf,
  ready,
  subscribe,
  sendEvent,
}: UseStageContentParams): UseStageContentResult {
  const items = useMemo<StageItem[]>(
    () =>
      buildStageItems({
        screens,
        whiteboardActive,
        whiteboardLabel,
        documents,
        videos,
        localPeerId,
      }),
    [screens, whiteboardActive, whiteboardLabel, documents, videos, localPeerId],
  )

  // Keyed rather than indexed, for the same reason the screen-share switcher
  // was: an item ending renumbers the list, and an index would slide the
  // viewer onto whatever inherited the slot.
  //
  // Which key is current lives in `stage-selection`, because the rule is more
  // than a key: it has to know what has arrived since the viewer last decided
  // anything. See that module for why — in short, without it the first thing
  // presented held the stage for the rest of the meeting and a screen share
  // started after a document was shown to nobody.
  const [stored, setStored] = useState(emptySelection)

  const keys = useMemo(() => items.map((i) => i.key), [items])

  // Reconciled during render rather than in an effect, so the first frame of a
  // share is already on the stage. Deriving it in an effect would paint one
  // frame of "nothing is being presented" every time anyone started anything.
  // `reconcile` returns its input unchanged once it has converged, which is
  // what makes setting state here terminate.
  const selection = reconcile(stored, keys)
  if (selection !== stored) setStored(selection)

  const selectedKey = selection.selectedKey
  const following = selection.following

  const active = useMemo(
    () => items.find((i) => i.key === selectedKey) ?? null,
    [items, selectedKey],
  )

  // ── Sync with Everyone ────────────────────────────────────────────────────
  // The rules live in `stage-sync`. The state is kept apart from the selection
  // because it says who drives the room, not what this client is looking at;
  // the two meet only where a push moves the selection.
  const [sync, setSync] = useState<StageSync>(null)
  const syncRef = useRef(sync)
  syncRef.current = sync
  const isSyncOwner = isSyncOwnerFor(sync, localPeerId)
  const followingLocked = isLockedFollower(sync, localPeerId)

  // Bumped on every push received, so the pull below runs again even when the
  // owner lands back on the tab it pushed last.
  const [pushSeq, setPushSeq] = useState(0)

  // A follower looking somewhere else on purpose. That is allowed, and left
  // alone until the owner's next push. Without this the pull below could not
  // tell a viewer who looked away from one the stage moved by itself because a
  // new share arrived.
  const glancedRef = useRef(false)

  const roleOfRef = useRef(roleOf)
  roleOfRef.current = roleOf

  const select = useCallback((key: string) => {
    // Choosing for yourself is the break-away. It is deliberately silent: the
    // host is not told, because a room full of "X stopped following" is noise
    // and nobody needs permission to read something again. Inside a sync it is
    // only a glance: the owner's next push brings the viewer back.
    if (isLockedFollower(syncRef.current, localPeerId)) glancedRef.current = true
    setStored((prev) => choose(prev, key))
  }, [localPeerId])

  const stopFollowing = useCallback(() => {
    // Someone else's sync has no opt-out: the chip offers none, and this stays
    // a no-op should anything call it anyway.
    if (isLockedFollower(syncRef.current, localPeerId)) return
    setStored(dropFollow)
  }, [localPeerId])

  // Resolve an incoming one-off pick against what this client can actually
  // see. The host may be looking at something that has not reached us yet, in
  // which case the pick is dropped rather than remembered: a stale one applied
  // later would yank the viewer long after the host moved on. A sync in force
  // is different, it IS still the host's pick, so its target is kept and
  // applied once it arrives (see the pull below).
  const itemsRef = useRef(items)
  itemsRef.current = items

  // Rides the generic `broadcast` envelope, not an event of its own. The room
  // channel has no catch-all clause, so an event an older signalling server
  // does not implement raises FunctionClauseError and takes the channel
  // process, and the sender's place in the room, down with it.
  const sendStage = useCallback(
    (body: Record<string, unknown>) => {
      sendEvent('broadcast', { body: { __inmeet: 'stage', by: localPeerId ?? undefined, ...body } })
    },
    [sendEvent, localPeerId],
  )

  useEffect(() => {
    if (!ready) return
    const off = subscribe('broadcast', (payload) => {
      const env = ((payload as { body?: StageSyncEnvelope })?.body ?? {}) as StageSyncEnvelope
      if (env.__inmeet !== 'stage') return
      if (env.by && localPeerId && env.by === localPeerId) return

      if (env.action === 'sync-off') {
        setSync((prev) => {
          const next = applyOff(prev, env.by, roleOfRef.current(env.by))
          if (next !== prev) vclog('stage', 'sync turned off', { by: env.by })
          return next
        })
        return
      }
      if (env.action !== 'sync') return

      if (env.mode === 'on' && env.by) {
        // A newcomer's copy of a sync this client is already in. Being pulled
        // back by it would undo every glance in the room whenever anyone joins.
        const current = syncRef.current
        if (env.reannounce && current && current.ownerPeerId === env.by) return
        const by = env.by
        glancedRef.current = false
        setSync((prev) => applyPush(prev, { by, ownerName: env.ownerName ?? '', syncId: env.syncId, kind: env.kind }))
        setPushSeq((n) => n + 1)
        vclog('stage', 'sync push received', { syncId: env.syncId, kind: env.kind })
        return
      }

      // A one-off pull, as mobile and older web clients send it.
      // Exact identity first. Falling back to the kind covers a sender that
      // cannot name one item of that kind: mobile tracks a single content view
      // per kind, so its whiteboard is `whiteboard` but its screen is just
      // `screen`, with no peer to point at. Landing the viewer on some screen
      // share is the intent; landing them on none is not.
      const target = resolveTarget(itemsRef.current, env.syncId, env.kind)
      if (!target) {
        vclog('stage', 'sync ignored, item not present here', { syncId: env.syncId, kind: env.kind })
        return
      }
      vclog('stage', 'following host', { syncId: env.syncId, kind: target.kind })
      setStored((prev) => follow(prev, target.key))
    })
    return off
  }, [ready, subscribe, localPeerId])

  // The owner's connection ending ends the sync, and a newcomer (or a tab that
  // reloaded) is told about one in force: room broadcasts do not replay, so
  // they would otherwise never hear of it. Same idea as the late-joiner
  // re-announce for a local recording.
  useEffect(() => {
    if (!ready) return
    const offDiff = subscribe('presence_diff', (payload) => {
      const diff = (payload ?? {}) as Parameters<typeof applyPresence>[1]
      setSync((prev) => applyPresence(prev, diff))
      const current = syncRef.current
      if (!current || !isSyncOwnerFor(current, localPeerId)) return
      if (!freshJoins(diff).some((id) => id !== localPeerId)) return
      const pick = resolveTarget(itemsRef.current, current.targetSyncId, current.targetKind)
      sendStage({
        action: 'sync',
        mode: 'on',
        reannounce: true,
        kind: pick?.kind ?? current.targetKind,
        syncId: pick?.syncId ?? current.targetSyncId,
        ownerName: current.ownerName,
      })
    })
    const offLeft = subscribe('media:peer_left', (payload) => {
      const { peerId } = (payload ?? {}) as { peerId?: string }
      setSync((prev) => ownerLeft(prev, peerId))
    })
    return () => { offDiff(); offLeft() }
  }, [ready, subscribe, localPeerId, sendStage])

  // The owner pushes every change of what it is looking at, whether chosen or
  // brought on by a new share arriving, so "host switches tab" needs no extra
  // click to reach the room.
  const lastPushedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isSyncOwner) {
      lastPushedRef.current = null
      return
    }
    if (!active || lastPushedRef.current === active.syncId) return
    lastPushedRef.current = active.syncId
    setSync((prev) => retarget(prev, active))
    sendStage({ action: 'sync', mode: 'on', kind: active.kind, syncId: active.syncId, ownerName: localName })
    vclog('stage', 'sync push', { syncId: active.syncId, kind: active.kind })
  }, [isSyncOwner, active, sendStage, localName])

  // Keep a follower on the owner's pick. Runs again on each push (which clears
  // a glance) and whenever items change, so a pick that had not reached this
  // client yet, a late joiner still loading, is applied the moment it arrives,
  // and a share starting cannot carry a follower off the pick by itself.
  const targetKey = followingLocked
    ? resolveTarget(items, sync?.targetSyncId, sync?.targetKind)?.key ?? null
    : null
  useEffect(() => {
    if (!targetKey || glancedRef.current) return
    if (selectedKey === targetKey && following) return
    setStored((prev) => follow(prev, targetKey))
  }, [targetKey, selectedKey, following, pushSeq])

  // A sync cannot outlive the switcher: below two items the bar is not shown,
  // so nobody could see it or turn it off.
  const prevCountRef = useRef(items.length)
  useEffect(() => {
    const before = prevCountRef.current
    prevCountRef.current = items.length
    const current = syncRef.current
    const next = applyItemCount(current, before, items.length)
    if (next === current) return
    if (isSyncOwnerFor(current, localPeerId)) sendStage({ action: 'sync-off' })
    setSync(next)
    vclog('stage', 'sync ended, fewer than two items', { count: items.length })
  }, [items.length, localPeerId, sendStage])

  // Leaving a sync drops whatever glance was in progress, and the follow it
  // put the viewer in: everyone keeps what is on screen, but nothing is in
  // force any more, so no "Following host" chip should outlive it.
  const wasSyncOnRef = useRef(false)
  useEffect(() => {
    if (sync) {
      wasSyncOnRef.current = true
      return
    }
    glancedRef.current = false
    if (wasSyncOnRef.current) {
      wasSyncOnRef.current = false
      setStored(dropFollow)
    }
  }, [sync])

  const canSync = isModerator && !!active && items.length > 0 && canToggleSyncFor(sync, localPeerId, isModerator)

  const syncRoom = useCallback(() => {
    if (!isModerator || !localPeerId) return
    const current = syncRef.current
    if (current) {
      if (!isSyncOwnerFor(current, localPeerId)) return
      sendStage({ action: 'sync-off' })
      setSync(null)
      vclog('stage', 'sync turned off by owner', {})
      return
    }
    const pick = itemsRef.current.find((i) => i.key === selectedKey) ?? itemsRef.current[itemsRef.current.length - 1]
    if (!pick) return
    // The first push goes out from the owner effect above, once this lands.
    setSync(startSync(localPeerId, localName, pick))
    vclog('stage', 'sync turned on', { syncId: pick.syncId })
  }, [isModerator, localPeerId, localName, selectedKey, sendStage])

  const turnOffSync = useCallback(() => {
    if (!hostCanTurnOffFor(syncRef.current, localPeerId, myRole)) return
    sendStage({ action: 'sync-off' })
    setSync(null)
    vclog('stage', 'sync turned off by host', {})
  }, [localPeerId, myRole, sendStage])

  return {
    items,
    active,
    activeKind: active?.kind ?? null,
    select,
    hasChoice: items.length > 1,
    syncRoom,
    canSync,
    isSyncOwner,
    syncOn: !!sync,
    syncOwnerName: sync?.ownerName || null,
    hostCanTurnOff: hostCanTurnOffFor(sync, localPeerId, myRole),
    turnOffSync,
    following,
    followingLocked,
    stopFollowing,
  }
}
